/**
 * frontend/src/pages/valuation/_OwnerReportCard.tsx
 *
 * VAL-PATH-P6b — step 6 "Get your report", owner only. Where the Snapshot
 * stands (recorded, then paid) and the one way to the full report: send the
 * value to a valuation signer — a status change only (D8: no email, no new
 * record), nothing re-entered. "Record and order my Snapshot" is the footer's
 * final action (U1), so it has one home and is not repeated here.
 *
 * Every string is from OWNER_APPROVALS.log (10-21 line 80, 10-22 line 95).
 */
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { ValuationEngagement } from '@/types/valuation'
import { SnapshotDownloadButton } from './_SnapshotDownload'
import { TierDownloads } from './_TierIncludes'

/** The reason an owner's record carries; nobody types it (approved 10-22 09:33). */
export const OWNER_ESTIMATE_REASON = "Owner's Estimated Value"
export const RECORD_AND_ORDER = 'Record and order my Snapshot'
export const SEND_TO_SIGNER = 'Send to a valuation signer'

/** Step 6's note above the full-report card, for the owner.
 *  `recorded` + `unpaid` = WF-B-1 (owner approved 2026-10-25 11:15).
 *  `have` / `signed` = VAL-68-OWNER-RULINGS B3, WORDING FOR OWNER APPROVAL: an owner who has paid
 *  for an option is told what they have, not "after payment" (which read as a failed payment). */
export const OWNER_NOTE = {
  recorded: 'Your estimate is recorded. You prepared it yourself, so it is an estimate, not a signed valuation.',
  unpaid: ' Your report will be available after payment.',
  have: ' You have {name}. It is ready under Get your report.',
  signed: ` A full signed report is prepared by a valuation signer. To ask for one, use "${SEND_TO_SIGNER}".`,
} as const

export function ownerDocumentNote(engagement: ValuationEngagement): string {
  const tiers = engagement.value_tiers ?? []
  const top = tiers.find(t => t.tier === engagement.value_tier) ?? tiers[tiers.length - 1]
  if (!top) return OWNER_NOTE.recorded + OWNER_NOTE.unpaid
  return OWNER_NOTE.recorded + OWNER_NOTE.have.replace('{name}', top.title) + OWNER_NOTE.signed
}

export function OwnerReportCard({
  engagement,
  recorded,
  snapshotOnFile,
  saving,
  onSend,
}: {
  engagement: ValuationEngagement
  /** A live owner record exists and nothing has changed since. */
  recorded: boolean
  /** The live record is the owner's own final estimate (FIX_VAL-SNAPSHOT-BUTTON-1); a signer's version has no Snapshot. */
  snapshotOnFile: boolean
  saving: boolean
  onSend: () => void
}) {
  const withSigner = engagement.status === 'in_cpa_review'
  return (
    <Card>
      <CardHeader><CardTitle>Get your report</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {snapshotOnFile && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            {engagement.snapshot_paid ? (
              <>
                <span className="text-sm text-pal-ink-body">Ready</span>
                <div className="flex flex-col gap-2 sm:w-64">
                  <SnapshotDownloadButton engagementId={engagement.valuation_engagement_id} />
                  <TierDownloads engagement={engagement} />
                </div>
              </>
            ) : engagement.value_tiers?.length ? (
              <>
                {/* VAL-TIERS-1-2: a Tier 1 / Tier 2 buyer has paid; no Snapshot, only their documents. */}
                <span className="text-sm text-pal-ink-body">Ready</span>
                <TierDownloads engagement={engagement} />
              </>
            ) : (
              <span className="text-sm text-pal-ink-body">Waiting for payment</span>
            )}
          </div>
        )}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          {withSigner ? (
            <span className="text-sm text-pal-ink-body">With the signer</span>
          ) : (
            <Button type="button" variant="outline" disabled={!recorded || saving} onClick={onSend}>
              {SEND_TO_SIGNER}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
